import {
	Vue, Component, Prop, Watch, Ref, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import mitt from 'mitt';
import Swal from 'sweetalert2';
import { httpClient } from 'utils/http';
import { TabEvent } from 'interfaces/app';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import TabulatorSwitchBox from 'components/Tabulator/TabulatorSwitchBox';
import CustomSelect from 'components/CustomSelect';
import TemplateService from 'services/TemplateService';
import ThemePageService from 'services/ThemePageService';
import LayoutService from 'services/LayoutService';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.LayoutTemplateLinkModel>>();
@Component({
	components: {
		TabulatorBtn,
		TabulatorSwitchBox,
		CustomSelect,
	},
	emits: ['routeLayoutTemplate'],
	mixins: [Template],
})
class LayoutTemplate extends Vue {
	@Ref('layoutTemplateTable')
	private readonly tableReference!: HTMLDivElement;

	private isLoading = false;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private showExistingPage = false;

	private showNewPage = false;

	private layoutdata = {} as DB.LayoutModel;

	private ExistingPagesData = {} as DB.LayoutModel;

	private serialnumber: undefined | number = undefined;

	private layoutTemplate: DB.TemplateModel[] = [];

	private themepageDate: DB.LayoutTemplateLinkModel[] = [];

	private table?: Tabulator;

	@Prop({
		type: String,
		required: true,
	})
	public readonly routeId!: string;

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'name',
				title: 'Name',
			},
			{
				title: 'Serialnumber',
				field: 'serialnumber',
			},
			{
				title: 'Page repeat',
				formatter: (cell: CellComponentExtended<DB.LayoutTemplateLinkModel>) => {
					const data = cell.getData();
					const instance = createInstance({
						component: TabulatorSwitchBox,
						props: {
							data,
							eventBus,
							checked: Boolean(data && data.repeat),
						},
					});
					return instance;
				},
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.LayoutTemplateLinkModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	@Watch('loggedIn')
	private async getTemplate(): Promise<DB.TemplateModel[] | undefined> {
		if (this.loggedIn) {
			// set the loading state
			this.isLoading = true;
			try {
				const resp = await TemplateService.getAll(new URLSearchParams({ limit: '0' }));
				this.layoutTemplate = resp;
				return this.layoutTemplate;
			} catch (error: any) {
				this.$toastError(error.message);
			} finally {
				this.isLoading = false;
			}
		}
		return undefined;
	}

	private switchBoxChanged(data: TabEvent<DB.LayoutTemplateLinkModel>['switchBoxChanged']): void {
		this.table?.alert('Loading..');
		const repeat = data.event ? 1 : 0;
		ThemePageService.update(
			data.params.id,
			{
				repeat,
			},
		)
			.then(() => undefined)
			.finally(() => {
				this.table?.clearAlert();
			}).catch((err) => {
				this.$toastError(err.message);
			});
	}

	protected mounted(): void {
		this.getTemplate();
		eventBus.on(
			'switchBoxChanged',
			this.switchBoxChanged,
		);
		eventBus.on(
			'editItem',
			this.editLayoutTemplate,
		);
		eventBus.on(
			'deleteItem',
			this.deleteLayoutTemplate,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'switchBoxChanged',
			this.switchBoxChanged,
		);
		eventBus.off(
			'editItem',
			this.editLayoutTemplate,
		);
		eventBus.off(
			'deleteItem',
			this.deleteLayoutTemplate,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const data = await LayoutService.getTemplateLinks(
				this.routeId,
				parameter,
			);
			this.themepageDate = data;
			this.serialnumber = data.length;
			this.table?.setData(data);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected async addPage(): Promise<void> {
		this.table?.alert('Loading...');
		try {
			const firstResonse = await TemplateService.create(
				{
					name: this.layoutdata.name,
				},
			);
			const secondResponse = await httpClient.post(
				'/api/layouttemplate',
				{
					layoutid: parseInt(
						this.routeId,
						10,
					),
					templateid: firstResonse.id,
					serialnumber: this.serialnumber,
				},
			);
			this.table?.addData(secondResponse.data);
		} catch (error: any) {
			this.$toastError(error.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected async addPages(): Promise<void> {
		this.isLoading = true;
		try {
			await httpClient.post(
				'/api/layouttemplate',
				{
					layoutid: parseInt(
						this.routeId,
						10,
					),
					templateid: this.ExistingPagesData.id,
				},
			);
		} catch (error: any) {
			this.$toastError(error.message);
		} finally {
			this.isLoading = false;
		}
	}

	private editLayoutTemplate(data: TabEvent<DB.LayoutTemplateLinkModel>['editItem']): void {
		this.$emit(
			'routeLayoutTemplate',
			data.params.templateid,
		);
	}

	private deleteLayoutTemplate(data: TabEvent<DB.LayoutTemplateLinkModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading..');
				// eslint-disable-next-line promise/no-nesting
				httpClient.delete(`/api/layouttemplate/${data.id}`).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(LayoutTemplate);
